// Everyday Services (commerce) — the PLATFORM controls behind the product:
// fail-closed availability switches and the reconciliation queue for orders
// whose provider outcome is unknown. Employees buy airtime / data / electricity
// from their own mobile money; one provider collects and delivers; KaziPeople
// never holds the money. Per-tenant enablement is the tenant's Features &
// Products toggle (product `commerce`) — not rebuilt here.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Empty, Form, Input, Modal, Radio, Skeleton, Space, Switch, Table, Tabs, Tag,
  Tooltip, Typography, message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { PageHeader, useConfirmWithReason } from '../../components/console';
import { useAppSelector } from '../../app/hooks';
import { selectHasPermission } from '../../features/auth/authSlice';
import { fmtDateTime } from '../../lib/format';
import * as api from '../../services/commerceOpsService';
import { ProviderProductsTab } from './ProviderProductsTab';
import {
  EVIDENCE_MAX, EVIDENCE_MIN, NOTE_MAX, OUTCOME_LABEL, SWITCH_REASON_MAX,
  allowedOutcomes, apiErrorMessage, buildSwitchGroups, formatMinor, needsToken, productLabel,
  purchaseSummary, reasonHelp, reasonLabel, shortId, summaryLine,
  type CommerceSwitch, type ResolveOutcome, type SwitchDef,
} from './everydayServicesLogic';

const { Text, Paragraph } = Typography;

type Load<T> =
  | { state: 'loading' }
  | { state: 'error'; error: string }
  | { state: 'ready'; data: T };

const NO_RESEND_HELP =
  'A paid order never moves provider and is never re-sent; record only what the provider\'s own records show.';

// ---------------------------------------------------------------------------
// Availability switches
// ---------------------------------------------------------------------------

const SwitchesTab: React.FC<{
  load: Load<CommerceSwitch[]>;
  onReload: () => void;
  onChanged: () => void;
  canEdit: boolean;
}> = ({ load, onReload, onChanged, canEdit }) => {
  const { confirm, dialog } = useConfirmWithReason();
  const [busy, setBusy] = useState<string | null>(null);

  const rows = load.state === 'ready' ? load.data : [];
  const groups = useMemo(() => buildSwitchGroups(rows), [rows]);
  const summary = useMemo(() => (load.state === 'ready' ? purchaseSummary(load.data) : null), [load]);

  const toggle = async (def: SwitchDef, next: boolean) => {
    const reason = await confirm({
      title: `Turn ${next ? 'ON' : 'OFF'}: ${def.label}`,
      message: next
        ? 'Purchases covered by this switch become possible again (if every other switch allows them).'
        : 'New purchases covered by this switch stop at once. Orders already paid are not affected.',
      okText: next ? 'Turn on' : 'Turn off',
      danger: !next,
      reasonRequired: true,
      reasonMaxLength: SWITCH_REASON_MAX,
    });
    if (reason === null) return;
    setBusy(def.scope);
    try {
      await api.setSwitch(def.scope, next, reason);
      message.success(`${def.label} is now ${next ? 'ON' : 'OFF'}`);
      onChanged();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not change the switch'), 6);
    } finally {
      setBusy(null);
    }
  };

  if (load.state === 'loading') {
    return (
      <Card size="small">
        <Alert type="info" showIcon message="Loading switches… purchase availability is not known yet." className="mb-3" />
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }
  if (load.state === 'error') {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load the switches — whether purchases are possible is UNKNOWN."
        description={load.error}
        action={<Button size="small" onClick={onReload}>Retry</Button>}
      />
    );
  }

  return (
    <div>
      {dialog}
      {summary && (
        <Alert
          type={!summary.possible ? 'error' : summary.warnings.length ? 'warning' : 'success'}
          showIcon
          className="mb-4"
          message={<Text strong>{summaryLine(summary)}</Text>}
          description={
            rows.length === 0
              ? 'No switch has been set yet. Every switch except the mobile networks is off until someone turns it on.'
              : 'Switches are fail-closed: global, country, product and provider are OFF unless turned on. Mobile networks are ON unless turned off.'
          }
        />
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {groups.map((g) => (
          <Card key={g.key} size="small" title={g.title} extra={<Text type="secondary" className="text-xs">{g.description}</Text>}>
            <div className="divide-y divide-gray-100 dark:divide-white/10">
              {g.items.map((def) => {
                const row = rows.find((r) => r.scope === def.scope);
                const on = row ? row.enabled : def.missingMeans;
                return (
                  <div key={def.scope} className="flex items-start justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <Space size={6} wrap>
                        <Text strong>{def.label}</Text>
                        <Tag color={on ? 'green' : 'red'}>{on ? 'ON' : 'OFF'}</Tag>
                        {!row && <Tag>default</Tag>}
                        <Text type="secondary" className="text-xs font-mono">{def.scope}</Text>
                      </Space>
                      {def.hint && <div><Text type="warning" className="text-xs">{def.hint}</Text></div>}
                      <div className="text-xs">
                        {row ? (
                          <Text type="secondary">
                            Changed {fmtDateTime(row.updated_at)}
                            {row.reason ? <> — “{row.reason}”</> : null}
                          </Text>
                        ) : (
                          <Text type="secondary">Never set — {def.missingMeans ? 'ON by default' : 'OFF until turned on'}</Text>
                        )}
                      </div>
                    </div>
                    <Tooltip title={canEdit ? undefined : 'You need commerce:ops to change switches'}>
                      <Switch
                        checked={on}
                        loading={busy === def.scope}
                        disabled={!canEdit || (busy !== null && busy !== def.scope)}
                        onChange={(next) => toggle(def, next)}
                        aria-label={`${def.label} switch`}
                      />
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reconciliation queue
// ---------------------------------------------------------------------------

interface ResolveForm {
  outcome?: ResolveOutcome;
  evidence_ref?: string;
  note?: string;
  token?: string;
  units?: string;
}

const ResolveModal: React.FC<{
  rc: api.ReconCase | null;
  onClose: () => void;
  onResolved: () => void;
}> = ({ rc, onClose, onResolved }) => {
  const [form] = Form.useForm<ResolveForm>();
  const [saving, setSaving] = useState(false);
  const outcome = Form.useWatch('outcome', form);
  const outcomes = rc ? allowedOutcomes(rc.reason) : [];
  const showToken = needsToken(rc?.product_type, outcome);

  const close = () => {
    form.resetFields(); // drops the token from memory
    onClose();
  };

  const submit = async () => {
    if (!rc) return;
    let v: ResolveForm;
    try {
      v = await form.validateFields();
    } catch {
      return; // field errors are shown inline
    }
    setSaving(true);
    try {
      const body: api.ResolveBody = {
        outcome: v.outcome as ResolveOutcome,
        evidence_ref: (v.evidence_ref || '').trim(),
        note: (v.note || '').trim() || undefined,
      };
      if (needsToken(rc.product_type, v.outcome)) {
        body.token = (v.token || '').trim();
        if (v.units?.trim()) body.units = v.units.trim();
      }
      await api.resolveReconCase(rc.id, body);
      message.success('Case resolved');
      form.resetFields();
      onResolved();
    } catch (e) {
      // Verbatim backend message (e.g. 409 "the provider may already have answered").
      // Never log the error object: the request body may hold the token.
      message.error(apiErrorMessage(e, 'Could not resolve the case'), 8);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!rc}
      title={rc ? `Resolve: ${reasonLabel(rc.reason)}` : 'Resolve'}
      okText="Record outcome"
      onOk={submit}
      onCancel={close}
      confirmLoading={saving}
      okButtonProps={{ disabled: outcomes.length === 0 }}
      destroyOnClose
      width={560}
    >
      {rc && (
        <>
          <Alert type="warning" showIcon className="mb-3" message={NO_RESEND_HELP} />
          {reasonHelp(rc.reason) && <Paragraph className="text-sm">{reasonHelp(rc.reason)}</Paragraph>}
          <Paragraph type="secondary" className="text-sm">
            {productLabel(rc.product_type)} · {formatMinor(rc.amount_minor, rc.currency)} · {rc.recipient} · provider{' '}
            <Text code>{rc.provider_code}</Text> · order <Text code>{shortId(rc.order_id)}</Text>
          </Paragraph>
          <Form form={form} layout="vertical" requiredMark>
            <Form.Item name="outcome" label="What do the provider's records show?" rules={[{ required: true, message: 'Choose the outcome' }]}>
              <Radio.Group>
                <Space direction="vertical">
                  {outcomes.map((o) => (
                    <Radio key={o} value={o}>{OUTCOME_LABEL[o]}</Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="evidence_ref"
              label="Evidence reference"
              extra="The provider's statement line, ticket number or portal reference."
              rules={[
                { required: true, whitespace: true, message: 'Cite the provider\'s evidence' },
                { min: EVIDENCE_MIN, message: `At least ${EVIDENCE_MIN} characters` },
                { max: EVIDENCE_MAX, message: `At most ${EVIDENCE_MAX} characters` },
              ]}
            >
              <Input maxLength={EVIDENCE_MAX} autoComplete="off" />
            </Form.Item>
            {showToken && (
              <>
                <Form.Item
                  name="token"
                  label="Electricity token"
                  extra="From the provider's records. The buyer needs it to load the meter."
                  rules={[{ required: true, whitespace: true, message: 'Enter the token from the provider' }]}
                >
                  <Input.Password autoComplete="off" visibilityToggle />
                </Form.Item>
                <Form.Item name="units" label="Units (optional)">
                  <Input autoComplete="off" placeholder="e.g. 12.5 kWh" />
                </Form.Item>
              </>
            )}
            <Form.Item name="note" label="Note (optional)">
              <Input.TextArea rows={3} maxLength={NOTE_MAX} showCount />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

const ReconTab: React.FC<{
  load: Load<api.ReconCase[]>;
  onReload: () => void;
  canResolve: boolean;
}> = ({ load, onReload, canResolve }) => {
  const [resolving, setResolving] = useState<api.ReconCase | null>(null);

  if (load.state === 'error') {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load the reconciliation queue — open cases are UNKNOWN, not zero."
        description={load.error}
        action={<Button size="small" onClick={onReload}>Retry</Button>}
      />
    );
  }

  const columns = [
    { title: 'Opened', dataIndex: 'opened_at', render: (v: string) => fmtDateTime(v), width: 150 },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      render: (v: string) => <Tooltip title={v}><Text code>{shortId(v)}</Text></Tooltip>,
    },
    { title: 'Product', dataIndex: 'product_type', render: (v: string) => productLabel(v) },
    { title: 'Provider', dataIndex: 'provider_code', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Recipient', dataIndex: 'recipient' },
    {
      title: 'Amount',
      dataIndex: 'amount_minor',
      align: 'right' as const,
      render: (v: number, r: api.ReconCase) => formatMinor(v, r.currency),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      render: (v: string, r: api.ReconCase) => (
        <Space size={4} wrap>
          <Tooltip title={reasonHelp(v)}>
            <Tag color={v === 'payment_outcome_unknown' || v === 'cancel_unconfirmed' ? 'gold' : 'orange'}>{reasonLabel(v)}</Tag>
          </Tooltip>
          {r.status === 'pending_provider_data' && <Tag color="blue">Awaiting provider data</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, r: api.ReconCase) => {
        const resolvable = allowedOutcomes(r.reason).length > 0;
        const why = !canResolve
          ? 'You need commerce:ops:resolve to resolve cases'
          : !resolvable
            ? 'This case type is resolved by operations outside the service'
            : undefined;
        return (
          <Tooltip title={why}>
            <Button size="small" type="primary" disabled={!!why} onClick={() => setResolving(r)}>Resolve</Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <Alert type="info" showIcon className="mb-3" message={NO_RESEND_HELP} />
      <Card size="small">
        <Table
          rowKey="id"
          size="small"
          loading={load.state === 'loading'}
          columns={columns}
          dataSource={load.state === 'ready' ? load.data : []}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          scroll={{ x: true }}
          locale={{
            emptyText:
              load.state === 'ready' ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No open cases — every order has a known outcome." />
              ) : (
                <span />
              ),
          }}
        />
      </Card>
      <ResolveModal
        rc={resolving}
        onClose={() => setResolving(null)}
        onResolved={() => {
          setResolving(null);
          onReload();
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const EverydayServicesPage: React.FC = () => {
  const canOps = useAppSelector(selectHasPermission('commerce:ops'));
  const canResolve = useAppSelector(selectHasPermission('commerce:ops:resolve'));
  const [switches, setSwitches] = useState<Load<CommerceSwitch[]>>({ state: 'loading' });
  const [cases, setCases] = useState<Load<api.ReconCase[]>>({ state: 'loading' });
  const [tab, setTab] = useState('switches');

  const loadSwitches = useCallback(async () => {
    setSwitches({ state: 'loading' });
    try {
      setSwitches({ state: 'ready', data: await api.listSwitches() });
    } catch (e) {
      setSwitches({ state: 'error', error: apiErrorMessage(e, 'Request failed') });
    }
  }, []);

  const loadCases = useCallback(async () => {
    setCases((c) => (c.state === 'ready' ? c : { state: 'loading' }));
    try {
      setCases({ state: 'ready', data: await api.listReconCases() });
    } catch (e) {
      setCases({ state: 'error', error: apiErrorMessage(e, 'Request failed') });
    }
  }, []);

  // A silent refresh after a toggle keeps the matrix on screen.
  const refreshSwitches = useCallback(async () => {
    try {
      setSwitches({ state: 'ready', data: await api.listSwitches() });
    } catch (e) {
      setSwitches({ state: 'error', error: apiErrorMessage(e, 'Request failed') });
    }
  }, []);

  useEffect(() => {
    loadSwitches();
    loadCases();
  }, [loadSwitches, loadCases]);

  const openCount = cases.state === 'ready' ? cases.data.length : null;

  return (
    <div style={{ maxWidth: 1200 }}>
      <PageHeader
        title="Everyday Services"
        subtitle="Airtime, data bundles and electricity bought by employees from their own mobile money. One provider collects and delivers; KaziPeople never holds the money."
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadSwitches();
              loadCases();
            }}
          >
            Refresh
          </Button>
        }
      />
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="These are platform-wide controls. Whether a company offers Everyday Services is set per tenant under Tenant → Features & Products."
      />
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'switches',
            label: 'Availability switches',
            children: <SwitchesTab load={switches} onReload={loadSwitches} onChanged={refreshSwitches} canEdit={canOps} />,
          },
          {
            key: 'recon',
            label: (
              <Space size={6}>
                Reconciliation queue
                {openCount !== null && openCount > 0 && <Badge count={openCount} overflowCount={199} />}
              </Space>
            ),
            children: <ReconTab load={cases} onReload={loadCases} canResolve={canResolve} />,
          },
          {
            key: 'products',
            label: 'Provider products',
            children: <ProviderProductsTab canEdit={canOps} />,
          },
        ]}
      />
    </div>
  );
};

export default EverydayServicesPage;
